import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { loadOnboarding, peekOnboarding } from '../../../services/agentOnboardingService';

/**
 * The signed-in agent's onboarding state for the dashboard gate.
 *
 * Reads the module-level cache in services/agentOnboardingService (filled by the
 * onboarding page and by every action that returns fresh state) and only goes to
 * the network when nothing is cached for this user; a stale answer is shown at
 * once and checked again in the background.
 *
 * Returns { onboarding, loading, error }.
 */
const useAgentReadiness = (enabled = true) => {
  const { currentUser } = useAuth();
  const userId = currentUser?.id ?? null;

  const [state, setState] = useState(() => {
    const hit = enabled ? peekOnboarding(userId) : null;
    return { onboarding: hit?.value ?? null, loading: enabled && !hit, error: null };
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    const hit = peekOnboarding(userId);
    if (hit) setState({ onboarding: hit.value, loading: false, error: null });
    else setState((prev) => ({ ...prev, loading: true, error: null }));

    if (!hit || hit.stale) {
      loadOnboarding(userId).then(
        (value) => {
          if (active) setState({ onboarding: value, loading: false, error: null });
        },
        (error) => {
          console.error('Checking the agent setup failed:', error);
          if (active) setState((prev) => ({ ...prev, loading: false, error }));
        },
      );
    }
    return () => {
      active = false;
    };
  }, [enabled, userId]);

  return state;
};

export default useAgentReadiness;
